import { useState, useEffect } from "react";
import { HashLoader } from "react-spinners";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import { getTickets } from "../../api/tickets";
import { TicketDetailsDrawer } from "./TicketDetailsDrawer";
import { ICONS } from "../../utils/icons";
import { COLORS } from "../../utils/colors";
import Status from "../../components/TicketStatus";
import timeAgo from "../../utils/timeAgo";
import { MainButton } from "../../components/MainButton";
import { CreateTicketDialog } from "./CreateTicketDialog";

const columns = [
  { headerName: "ID", field: "id", flex: 1 },
  { headerName: "Customer", field: "name", flex: 1 },
  { headerName: "Category", field: "category_name", flex: 1 },
  {
    headerName: "Status",
    field: "status",
    flex: 1,
    renderCell: (params) => <Status status={params.value} />,
  },
  { headerName: "Type", field: "type", flex: 1 },
  { headerName: "Bought", field: "date", flex: 1 },
  {
    headerName: "Listing Price",
    field: "amount",
    flex: 1,
  },
  {
    headerName: "QR Code",
    field: "qr_hash",
    flex: 1,
  },
];

export const AllTickets = ({
  event,
  userData,
  handleSnackbar,
  numberOfTickets,
  setNumberOfTickets,
  dayPeriod,
}) => {
  const [loading, setLoading] = useState(false);
  const [openTicketDialog, setTicketDialog] = useState(false);
  const [ticket, setTicket] = useState({});
  const [ticketsObject, setTicketsObject] = useState(null);
  const [createTicketPopup, setCreateTicketPopup] = useState(false);
  const [data, setData] = useState([]);
  const [showScannedTickets, setShowScannedTickets] = useState(false);
  const [showTicketsNotScanned, setShowTicketsNotScanned] = useState(false);

  useEffect(async () => {
    setLoading(true);

    if (userData) {
      fetchData();
    }
  }, [dayPeriod]);

  const fetchData = async () => {
    const { data, error } = await getTickets({
      apiKey: userData.operators[0].api_key,
      dayPeriod,
      eventID: event._id,
    });

    setLoading(false);
    if (error) {
      if (!error.message.includes("No ticket found")) {
        handleSnackbar(error.message, "error");
      }
    }

    if (data) {
      setData(data);
      processDataGridData(data);
      setLoading(false);
    }
  };

  const handleClick = (ticket) => {
    setTicket(ticket);
    setTicketDialog(true);
  };

  const processDataGridData = (data) => {
    const array = [];

    data.forEach((ticket, index) => {
      let ticketPrice;
      if (ticket.last_price?.amount || ticket.last_price?.amount === 0) {
        ticketPrice = `${
          ticket.last_price.amount
        } ${ticket.last_price.currency.toUpperCase()}`;
      } else {
        ticketPrice = `N/A`;
      }

      const customerData = {
        ...ticket,
        name: ticket.customer.email,
        date: timeAgo(ticket.createdAt),
        amount: ticketPrice,
        index: index + 1,
        id: ticket._id,
        qr_hash: ticket.qr_hash,
      };

      array.push(customerData);
    });

    setTicketsObject({
      columns: columns,
      rows: array,
    });
  };

  useEffect(() => {
    if (!showScannedTickets) {
      processDataGridData(data);
    } else {
      const scannedTickets = data.filter(
        (ticket) => ticket.history.scan_history.length > 0
      );

      processDataGridData(scannedTickets);
    }
  }, [showScannedTickets]);

  useEffect(() => {
    if (!showTicketsNotScanned) {
      processDataGridData(data);
    } else {
      const ticketsNotScanned = data.filter(
        (ticket) => ticket.history.scan_history.length === 0
      );

      processDataGridData(ticketsNotScanned);
    }
  }, [showTicketsNotScanned]);

  return (
    <div className="all-tickets-container">
      {openTicketDialog && (
        <TicketDetailsDrawer
          open={openTicketDialog}
          setOpen={setTicketDialog}
          ticket={ticket}
          userData={userData}
          handleSnackbar={handleSnackbar}
          fetchData={fetchData}
          setNumberOfTickets={setNumberOfTickets}
        />
      )}
      {createTicketPopup && (
        <CreateTicketDialog
          openPopup={createTicketPopup}
          setOpenPopup={setCreateTicketPopup}
          userData={userData}
          handleSnackbar={handleSnackbar}
          apiKey={userData?.operators[0]?.api_key}
          fetchData={fetchData}
          event={event}
        />
      )}
      <div
        className="standard-container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Tickets Sold</h2>
          <p id="subTitle" style={{ marginBottom: "20px" }}>
            Total number of tickets sold.
          </p>
          <MainButton
            text={"Create ticket"}
            color={COLORS.black}
            hoverColor={COLORS.black}
            textColor={COLORS.white}
            borderColor={COLORS.black}
            shadowColor={COLORS.mainAccent}
            textHoverColor={COLORS.lightGrey}
            onClickAction={() => setCreateTicketPopup(true)}
            width={"fit-content"}
          >
            {ICONS.ticketWhite20}
          </MainButton>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "15px",
          }}
        >
          <div className="small-container">
            <div
              className="container-row"
              style={{
                width: "-webkit-fill-container",
                justifyContent: "space-between",
              }}
            >
              <p id="subheader">Total tickets sold</p>
              {ICONS.ticketAccent20}
            </div>
            <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
              {loading ? (
                <HashLoader color={COLORS.mainAccent} size={30} />
              ) : (
                numberOfTickets
              )}
            </h2>
          </div>
          <div className="small-container">
            <div
              className="container-row"
              style={{
                width: "-webkit-fill-container",
                justifyContent: "space-between",
              }}
            >
              <p id="subheader">Event Maximum Capacity</p>
              {ICONS.threePeopleAccent20}
            </div>
            <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
              {event?.capacity}
            </h2>
          </div>
        </div>
      </div>

      {ticketsObject && (
        <Box sx={{ height: 720, width: "100%" }}>
          <DataGrid
            {...ticketsObject}
            slots={{
              toolbar: () => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <GridToolbar />
                  <FormGroup
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showScannedTickets}
                          onClick={() => {
                            setShowScannedTickets(
                              showTicketsNotScanned === true
                                ? showScannedTickets
                                : !showScannedTickets
                            );
                          }}
                        />
                      }
                      label="Only scanned tickets"
                      sx={{
                        "& .MuiTypography-root": {
                          fontFamily: "AuthenticSans90",
                          fontSize: "14px",
                          color: COLORS.mainAccent,
                        },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showTicketsNotScanned}
                          onClick={() => {
                            setShowTicketsNotScanned(
                              showScannedTickets === true
                                ? showTicketsNotScanned
                                : !showTicketsNotScanned
                            );
                          }}
                        />
                      }
                      label="Only tickets not scanned"
                      sx={{
                        "& .MuiTypography-root": {
                          fontFamily: "AuthenticSans90",
                          fontSize: "14px",
                          color: COLORS.mainAccent,
                        },
                      }}
                    />
                  </FormGroup>
                </div>
              ),
            }}
            onCellClick={(e) => handleClick(e.row)}
            slotProps={{
              columnMenu: {
                background: "red",
                counter: ticketsObject.rows.length,
              },
            }}
            style={{ fontFamily: "AuthenticSans90", marginBottom: "40px" }}
            sx={{
              "& .MuiDataGrid-toolbarContainer": {
                "& .MuiButton-text": {
                  color: COLORS.mainAccent,
                  textTransform: "capitalize",
                  fontFamily: "AuthenticSans90",
                  fontSize: "14px",
                },
              },
            }}
          />
        </Box>
      )}
      <div style={{ height: "50px" }} />
    </div>
  );
};
